import { AxiosRequestConfig } from "axios";
import { ApplicationsCard } from "pages/Applications/ApplicationsCard";
import { Text } from "components";
import DashboardNavbar from "components/DashboardNavbar";
import { Search } from "lucide-react";
import { useDebounce } from "pages/CreateDeal/newdeal";
import {
  getRelativeDate,
  isValidIndianMobile,
} from "pages/Opportunities/utils/featureRule";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactSelect, {
  components,
  MenuProps,
  OptionProps,
  SingleValue,
  StylesConfig,
} from "react-select";
import {
  setContactPersonName,
  setContactPersonPhone,
} from "redux/features/CreateOpportunity";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { homeStats } from "service/moneeflo";
import type { HomePeriod, HomeStats } from "service/moneeflo";
// Same signature, answered by the new backend. See legacy-contact-api.ts.
import {
  searchContact1,
  SearchContactReqType,
} from "service/moneeflo/legacy-contact-api";
import { toast } from "sonner";
import { formatIndianNumber } from "../../utils";
import { buttonData, ContactData } from "./dashboard-data";
import { LEVEL_2, LEVEL_3 } from "common/constants";
import { checkLevel } from "common/rules";
import { RowDetailsSheet } from "pages/Opportunities/opportunitydetails";
import PreparequoteHomepageLevel1 from "./PrepQuote/PrepareQuoteHomepage";

interface CustomMenuProps extends MenuProps<ContactData, false> {
  business?: boolean; // Add the custom prop
  inputValue?: string;
}

const CustomMenu: React.FC<CustomMenuProps> = ({
  children,
  business,
  inputValue,
  ...props
}) => {
  const navigate = useNavigate();
  const { pipelineType } = useAppSelector((state) => state.CreateOpportunity);

  const handleAddContact = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default behavior
    e.stopPropagation(); // Stop event bubbling
    navigate(buttonData[pipelineType].href, {
      state: {
        contact_person_phone: inputValue,
      },
    });
  };
  return (
    <components.Menu {...props}>
      {!checkLevel(LEVEL_2) && !business && (
        <div className="bg-[#F9F9F9] h-[33px] p-2 w-full justify-end flex gap-2">
          <div className="flex items-center gap-1">
            <p className="bg-[#E1EBFC] p-1 rounded font-normal text-xs leading-[16.8px]">
              P
            </p>
            <p className="font-normal text-xs leading-[16.8px] text-[#999999]">
              Partner
            </p>
          </div>
          <div className="flex items-center gap-1">
            <p className="bg-[#E1EBFC] p-1 rounded font-normal text-xs leading-[16.8px]">
              C
            </p>
            <p className="font-normal text-xs leading-[16.8px] text-[#999999]">
              Corporate
            </p>
          </div>
        </div>
      )}
      {children}
      {(!checkLevel(LEVEL_2) || props.options.length === 0) &&
        (props.selectProps.inputValue.length !== 10 ||
          !props.options.length) && (
          <div>
            <button
              className="bg-blue-700 w-full absolute text-white-A700 p-2 rounded-b-xl"
              onClick={handleAddContact}
              onTouchEnd={(e) => {
                e.preventDefault(); // Prevent default touch behavior
                handleAddContact(e as unknown as React.MouseEvent);
              }}
            >
              {buttonData[pipelineType].label}
            </button>
          </div>
        )}
    </components.Menu>
  );
};

const CustomOption: React.FC<OptionProps<ContactData, false>> = ({
  innerProps,
  label,
  data,
  isFocused,
}) => (
  <div
    {...innerProps}
    style={{
      padding: "8px 12px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      cursor: "pointer",
      backgroundColor: isFocused ? "#F3F7FF" : "inherit",
      color: data.status === "Blocked" ? "red" : "inherit",
    }}
  >
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <tbody>
        <tr>
          <td style={{ width: "40%" }}>
            <div className=" first-letter:uppercase">
              {data.partnerType && !data?.companyName && (
                <span className="bg-[#E1EBFC] p-1 mr-2 rounded font-normal text-xs leading-[16.8px]">
                  P
                </span>
              )}
              {data.companyName && (
                <span className="bg-[#E1EBFC] p-1 mr-2 rounded font-normal text-xs leading-[16.8px]">
                  C
                </span>
              )}
              {data.partnerType && !data?.companyName
                ? data?.name
                : data?.companyName
                  ? data?.companyName
                  : data?.contactName}
            </div>
          </td>
          <td style={{ width: "27%", textAlign: "left" }}>
            <div>{}</div>
          </td>
          {/* <td style={{ width: '25%' }} className="">
            <div style={{ fontSize: '0.8em', color: '#666' }}>{data.location ? data?.location : "N/A"}</div>
          </td> */}
          <td style={{ width: "33%", textAlign: "right" }}>
            <div
              style={{
                display: "inline-block",
                padding: "2px 6px",
                borderRadius: "12px",
                fontSize: "0.8em",
                color: "black",
              }}
            >
              {data?.customer_types?.some((item) =>
                ["Contact", "Customer"].includes(item),
              )
                ? "Individual"
                : data.partnerType
                  ? data?.partnerType.split("_").join(" ")
                  : data?.companyName
                    ? data?.type
                    : data?.contactType}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

function SearchAndTab() {
  const [inputValue, setInputValue] = useState<string>("");
  const [options, setOptions] = useState<Partial<ContactData[]>>([]);
  const [checkContactType, setCheckContactType] = useState<{
    [key: string]: boolean;
  }>({
    individual: false,
    business: false,
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleChange = (newValue: SingleValue<ContactData>) => {
    if (!newValue) return;
    if (newValue?.companyName && newValue?.role === "lead") {
      navigate(`/leads/corporate/${newValue?.value}`);
      return;
    }
    if (
      newValue?.customer_types?.includes("Contact") &&
      newValue?.customer_types?.includes("Customer")
    ) {
      navigate(`/customerdetails/${newValue?.value}`);
      return;
    }
    if (newValue?.partnerType && !newValue?.companyName) {
      if (newValue?.role === "lead") {
        navigate(`/leads/partner/${newValue?.value}`);
      } else navigate(`/partnerdetails/${newValue?.value}`);
    } else if (!newValue?.companyName && !newValue?.isCustomer) {
      navigate(`/leads/contact/${newValue?.value}`);
    } else if (!newValue?.companyName && newValue?.isCustomer) {
      if (
        !newValue?.linked_customer_id ||
        Number(newValue?.linked_customer_id) === 0
      ) {
        navigate(`/leads/contact/${newValue?.value}`);
      } else {
        navigate(`/customerdetails/${newValue?.value}`);
      }
    }
  };

  const fetchSearchResults = async () => {
    if (checkLevel(LEVEL_2) && inputValue.length < 10) {
      return;
    }
    if (!inputValue) {
      return;
    }
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const payload: AxiosRequestConfig<SearchContactReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      params: {
        searchQuery: inputValue,
        contactType: checkContactType?.individual
          ? "individual"
          : checkContactType?.business
            ? "business"
            : null,
      },
    };
    try {
      if (inputValue === "") return;
      const response = (await searchContact1(payload)) as any;
      if (response?.data?.length === 0) {
        setOptions(() => []);
        return;
      }
      let newOptions: ContactData[];
      if (checkLevel(LEVEL_2)) {
        newOptions = response?.data?.combined_record?.map((item: any) => ({
          value: item?.id,
          label: item?.contactName,
          contactName: item?.full_name || item?.contactName,
          contactPhone: item?.phone_number || item?.contactNumber,
          email: item?.contactEmail,
          role: item?.status,
          location: item?.contactAddress?.[0],
          status: item?.status,
          type: item?.contactPersonType,
          partnerType: item?.type,
          contactType: item?.contactPersonType,
          name: item?.name,
          phone: item?.phone,
          companyName: item?.company_name,
          lead_status: item?.lead_status,
          isCustomer: item?.customer,
          linked_customer_id: item?.linked_customer_id,
          customer_types: item?.customer_types || [],
        }));
      } else
        newOptions = response?.data?.combined_record?.map((item: any) => ({
          value: item?.id,
          label: item?.contactName,
          contactName: item?.full_name || item?.contactName,
          contactPhone: item?.phone_number || item?.contactNumber,
          email: item?.contactEmail,
          role: item?.status,
          location: item?.contactAddress?.[0],
          status: item?.status,
          type: item?.contactPersonType,
          partnerType: item?.type,
          contactType: item?.contactPersonType,
          name: item?.name,
          phone: item?.phone,
          companyName: item?.company_name,
          lead_status: item?.lead_status,
          isCustomer: item?.customer,
          linked_customer_id: item?.linked_customer_id,
          customer_types: item?.customer_types || [],
        }));
      setOptions(newOptions);
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Search limit exceeded.", {
          description:
            "You have exceeded the limit of requests. Please try again later.",
        });
      }
      console.error("Error fetching options:", error);
    }
  };

  useDebounce(() => fetchSearchResults(), 500, [inputValue]);

  const customStyles: StylesConfig<ContactData> = {
    control: (provided, state) => ({
      ...provided,
      zIndex: 0,
      borderRadius: "1.75rem",
      height: "3rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      paddingLeft: "40px",
      borderColor: state.isFocused ? "#165FE3" : "#d2d2d2",
      boxShadow: "none",
      width: "100%",
    }),
    menu: (provided) => ({
      ...provided,
      padding: 0,
      maxHeight: "300px",
      display: (() => {
        if (checkLevel(LEVEL_2) && inputValue.length < 10) {
          return "none";
        }
        return inputValue ? "block" : "none";
      })(),
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      width: "100%",
      maxHeight: "240px",
    }),
    option: (provided) => ({
      ...provided,
      width: "100%",
      display: "flex",
      borderRadius: "12px",
      justifyContent: "space-between",
      background: "#000000",
    }),
    placeholder: (provided) => ({
      ...provided,
      "@media (max-width: 768px)": {
        fontSize: "16px",
      },
      "@media (max-width: 480px)": {
        fontSize: "13px",
      },
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      display: "none",
    }),
  };

  const placeholder = (): string => {
    if (checkLevel(LEVEL_2)) {
      return "Search by mobile number";
    }
    if (checkLevel(LEVEL_3)) return "Search by name or mobile number";

    return "Search by name or mobile number";
  };

  const handleCheckChange = (
    e: ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    const check = e.target.checked;
    if (type === "individual") {
      setCheckContactType((prev) => ({
        ...prev,
        individual: check,
        business: false,
      }));
    }
    if (type === "business") {
      setCheckContactType((prev) => ({
        ...prev,
        business: check,
        individual: false,
      }));
    }
  };

  return (
    <div className="p-4 lg:py-10 lg:px-5 flex items-center flex-col gap-8 rounded-xl bg-white-A700">
      <div className="flex gap-2 items-center flex-col w-full sm:max-w-xl max-w-2xl">
        {!checkLevel(LEVEL_2) && (
          <div className="flex gap-2 items-center w-full px-4 justify-center lg:justify-end">
            <div className="flex gap-2 items-center">
              <input
                onChange={(e) => handleCheckChange(e, "individual")}
                checked={checkContactType?.individual}
                className="rounded-sm disabled:opacity-40"
                type="checkbox"
                name="individual"
                id="individual"
              />
              <label
                aria-disabled={checkContactType?.business}
                className="select-none disabled:opacity-40 font-medium text-sm leading-[19.6px] text-[#666666]"
                htmlFor="individual"
              >
                Individual
              </label>
            </div>
            <div className="flex gap-2 items-center">
              <input
                onChange={(e) => handleCheckChange(e, "business")}
                checked={checkContactType?.business}
                className="rounded-sm disabled:opacity-40"
                type="checkbox"
                name="business"
                id="business"
              />
              <label
                aria-disabled={checkContactType?.individual}
                className="select-none disabled:opacity-40 font-medium text-sm leading-[19.6px] text-[#666666]"
                htmlFor="business"
              >
                Business
              </label>
            </div>
          </div>
        )}
        <div className=" relative flex items-center w-full">
          <ReactSelect<ContactData>
            placeholder={placeholder()}
            styles={customStyles}
            components={{
              Option: CustomOption,
              Menu: (menuProps) => (
                <CustomMenu
                  {...menuProps}
                  inputValue={inputValue}
                  business={checkContactType?.individual}
                />
              ),
            }}
            isClearable
            isSearchable
            name="company"
            inputValue={inputValue}
            onChange={handleChange}
            options={options as ContactData[]}
            onInputChange={(raw) => {
              // Clean the value rather than refuse it.
              //
              // This used to reject anything that was not a letter or a
              // digit, which meant a space was impossible — "Udit Chawla"
              // could not be typed — and pasting a name silently did
              // nothing at all, because one bad character threw the whole
              // paste away. Stripping what we cannot use and keeping the
              // rest is what makes paste work.
              let value = raw.replace(/[^a-zA-Z0-9 .'-]/g, "");

              // A pasted number arrives as "+91 98765 43210" as often as
              // not, so a value that is only digits and separators is
              // treated as a phone number and reduced to its digits.
              const looksLikePhone = value.trim() !== "" && !/[a-zA-Z]/.test(value);
              if (looksLikePhone) {
                value = value.replace(/\D/g, "").slice(0, 10);
              } else {
                value = value.replace(/\s{2,}/g, " ").slice(0, 60);
              }

              const digitsOnly = /^\d*$/.test(value);

              if (value.length === 0) {
                dispatch(setContactPersonPhone(""));
                dispatch(setContactPersonName(""));
                setOptions([]);
              }

              setInputValue(value);

              // only set phone when it's exactly 10 digits and no alphabets
              if (
                digitsOnly &&
                value.length === 10 &&
                isValidIndianMobile(value)
              ) {
                dispatch(setContactPersonPhone(value));
              }
            }}

            className="w-full disabled:opacity-40"
            filterOption={null}
          />
          <Search className="absolute left-2 h-3.5" />
        </div>
      </div>
    </div>
  );
}

const DashboardPage: React.FC = () => {
  // skipcq: JS-0323, JS-0323
  const [state, setState] = useState<any>();
  const [, setLoading] = useState<boolean>(false);
  const [showStats, setShowStats] = useState<boolean>(false);
  const [tabSwitch, setTabSwitch] = useState<boolean>(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [loadImg, setLoadImg] = useState(true);
  const [isRowDetailsOpen, setIsRowDetailsOpen] = useState(false);
  const [isShow, setIsShow] = useState<boolean>(false);
  const id = new URLSearchParams(window.location.search).get("id");
  const product = new URLSearchParams(window.location.search).get("product");
  const create_date = new URLSearchParams(window.location.search).get(
    "create_date",
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScrollDown = () => {
    const scrollContainer = document.querySelector(".scroll-container");
    if (scrollContainer) {
      const scrollAmount = 500; // Adjust this value to control how far it scrolls
      scrollContainer.scrollBy({
        top: scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleScroll = () => {
    setShowScrollIndicator(false);
  };

  useEffect(() => {
    const scrollDiv = scrollRef.current;
    if (scrollDiv) {
      scrollDiv.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollDiv) {
        scrollDiv.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  /**
   * Stats from the new backend, shaped the way this screen already reads them:
   * `stats` as named rows with today/week/month, `deal_stats` with a count.
   * Xano returned those arrays directly; the numbers now come from commission
   * rows, so the shape is built here rather than upstream.
   */
  /** The whole payload, so switching tab re-reads it rather than refetching. */
  const [homeFigures, setHomeFigures] = useState<HomeStats | null>(null);
  /** Remittance is the default, being most of the business. */
  const [statsCategory, setStatsCategory] = useState("remittance");

  /**
   * The stat cards for whichever product tab is selected.
   *
   * A category with no business in a period shows zero rather than
   * vanishing, so the tabs do not appear and disappear as the day goes on.
   */
  const statsForCategory = useMemo(() => {
    if (!homeFigures) return null;
    const rupees = (minor: string) => Number(minor) / 100;
    const zero = {
      grossMinor: "0",
      netMinor: "0",
      volumeMinor: "0",
      orders: 0,
      marginRate: "0",
    };
    const slice = (period: HomePeriod) =>
      period.byCategory?.find((c) => c.category === statsCategory) ?? zero;

    const t = slice(homeFigures.today);
    const w = slice(homeFigures.week);
    const m = slice(homeFigures.month);

    return [
      { name: "Gross Revenue", today: rupees(t.grossMinor), week: rupees(w.grossMinor), month: rupees(m.grossMinor) },
      { name: "Net Revenue", today: rupees(t.netMinor), week: rupees(w.netMinor), month: rupees(m.netMinor) },
      { name: "Order Volume", today: rupees(t.volumeMinor), week: rupees(w.volumeMinor), month: rupees(m.volumeMinor) },
      { name: "Number of Orders", today: t.orders, week: w.orders, month: m.orders },
      { name: "My Margin", today: Number(t.marginRate), week: Number(w.marginRate), month: Number(m.marginRate) },
    ];
  }, [homeFigures, statsCategory]);

  /**
   * The product tabs.
   *
   * The two lines the business sells are always shown, in that order,
   * whether or not either has business today — a tab bar that appears
   * and disappears with the data is worse than one showing a zero.
   * Anything else that turns up in the figures is appended.
   */
  const statsCategories = useMemo(() => {
    const LABELS: Record<string, string> = {
      remittance: "Remittance",
      forex: "Forex",
    };
    const ordered = ["remittance", "forex"];
    const extra = new Set<string>();
    for (const period of [homeFigures?.month, homeFigures?.week, homeFigures?.today]) {
      for (const c of period?.byCategory ?? []) {
        if (!ordered.includes(c.category)) extra.add(c.category);
      }
    }
    return [...ordered, ...[...extra].sort()].map((value) => ({
      value,
      label: LABELS[value] ?? value.charAt(0).toUpperCase() + value.slice(1),
    }));
  }, [homeFigures]);

  const getDashboardStatsData = () => {
    setLoading(true);
    homeStats()
      .then((home) => {
        const rupees = (minor: string) => Number(minor) / 100;
        setHomeFigures(home);
        setState({
          stats: [
            {
              name: "Gross Revenue",
              today: rupees(home.today.grossMinor),
              week: rupees(home.week.grossMinor),
              month: rupees(home.month.grossMinor),
            },
            {
              name: "Net Revenue",
              today: rupees(home.today.netMinor),
              week: rupees(home.week.netMinor),
              month: rupees(home.month.netMinor),
            },
            {
              name: "Order Volume",
              today: rupees(home.today.volumeMinor),
              week: rupees(home.week.volumeMinor),
              month: rupees(home.month.volumeMinor),
            },
            {
              name: "Number of Orders",
              today: home.today.orders,
              week: home.week.orders,
              month: home.month.orders,
            },
            {
              name: "My Margin",
              today: Number(home.today.marginRate),
              week: Number(home.week.marginRate),
              month: Number(home.month.marginRate),
            },
          ],
          deal_stats: [
            { name: "Deals unutilized", today: home.deals.unutilized },
          ],
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return {
        greet: "Good Morning",
        image: "images/overview/day.png",
        color: "#5597B2",
      };
    } else if (hour >= 12 && hour < 18) {
      return {
        greet: "Good Afternoon",
        image: "images/overview/evening.png",
        color: "#FFA52D",
      };
    } else {
      return {
        greet: "Good Evening",
        image: "images/overview/night.png",
        color: "#FFA52D",
      };
    }
  };

  useEffect(() => {
    getDashboardStatsData();
  }, []);

  const navLinks = {
    current: {
      label: "All Overview",
      icon: "",
    },
    left: [
      {
        label: "Remit",
        href: "/remit",
        icon: "images/overview/remit.svg",
        logo: "images/overview/remit-text.svg",
        enabled: true,
      },
      {
        label: "Forex",
        href: "/overview",
        icon: "images/overview/forex.svg",
        logo: "images/overview/forex-text.svg",
        enabled: false,
      },
      {
        label: "Corporate",
        href: "/overview",
        icon: "images/overview/corporate.svg",
        logo: "images/overview/corporate-text.svg",
        enabled: false,
      },
    ],
    right: [
      {
        label: "Notification",
        visible: false,
        href: "#",
        icon: "images/overview/bell.svg",
        submenu: [],
        enabled: true,
      },
      // {
      //   label: "Settings",
      //   visible: false,
      //   href: "#",
      //   icon: "images/overview/settings.svg",
      //   submenu: [],
      //   enabled: true,
      // },
      {
        label: "Profile",
        visible: false,
        href: "#",
        icon: "images/overview/profile.svg",
        submenu: [
          {
            label: "My Profile",
            href: "/profile",
            icon: "images/overview/myprofile.svg",
          },
          ...(checkLevel(LEVEL_2)
            ? [
                {
                  label: "My Company",
                  href: "/mycompany",
                  icon: "images/MyCompany.svg",
                },
              ]
            : []),
          {
            label: "Logout",
            href: "/logout",
            icon: "images/overview/logout.svg",
          },
        ],
        enabled: true,
      },
      // {
      //   label: "Help",
      //   visible: true,
      //   href: "#",
      //   icon: "images/overview/wrench.svg",
      //   submenu: [
      //     {
      //       label: "Documents",
      //       href: "#",
      //       icon: "images/overview/myprofile.svg",
      //     },
      //     {
      //       label: "Youtube Tutorials",
      //       href: "#",
      //       icon: "images/overview/myprofile.svg",
      //     },
      //     {
      //       label: "Contact Support",
      //       href: "#",
      //       icon: "images/sidebar_img/contact.svg",
      //     },
      //   ],
      //   enabled: true,
      // },
      // {
      //   label: "plus",
      //   visible: false,
      //   href: "#",
      //   icon: "images/overview/btn.svg",
      //   submenu: [
      //     {
      //       label: "Create Deals",
      //       href: "/createnewdeal",
      //       icon: "/images/sidebar_img/remit.svg",
      //     },
      //     {
      //       label: "Create Orders",
      //       href: "/createorder",
      //       icon: "/images/sidebar_img/remit.svg",
      //     },
      //     {
      //       label: "View Orders",
      //       href: "/remit/orders",
      //       icon: "/images/sidebar_img/remit.svg",
      //     },
      //     {
      //       label: "Create FX Quote",
      //       href: "/createforexQuotation",
      //       icon: "/images/sidebar_img/remit.svg",
      //     },
      //     {
      //       label: "Create Remit Quote",
      //       href: "/createRemittanceQuotation",
      //       icon: "/images/sidebar_img/remit.svg",
      //     },
      //   ],
      //   enabled: true,
      // },
    ],
  };

  useEffect(() => {
    setIsRowDetailsOpen(!!id && !!product && !!create_date);
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = greeting().image;
    img.onload = () => {
      setLoadImg(false);
    };
  }, [greeting().image]);
  return (
    // skipcq: JS-0415
    <>
      {id && product && create_date && (
        <RowDetailsSheet
          isOpen={isRowDetailsOpen}
          onOpenChange={setIsRowDetailsOpen}
          rowData={{
            id,
            product,
            createDate: getRelativeDate(Number(create_date)),
          }}
          getOpportunityList={() => {}}
        />
      )}
      <div className="bg-gray-100 h-full sm:h-auto overflow-auto flex gap-4 flex-col font-inter w-full">
        <DashboardNavbar navLinks={navLinks} />

        <section className="flex flex-col h-full overflow-hidden sm:overflow-auto gap-4 w-full">
          <SearchAndTab />

          {/* Main content area that will scroll */}
          <div className="flex flex-1 overflow-hidden sm:overflow-auto gap-4 sm:flex-col-reverse">
            <div className="w-1/2 sm:w-full h-full overflow-hidden sm:overflow-auto flex flex-col gap-4">
            <div className="sm:hidden">
              <ApplicationsCard />
            </div>
            <div className="flex-1 min-h-0 overflow-hidden sm:overflow-auto flex flex-col bg-white-A700 rounded-xl">
                <div className="p-4 border-b">
                  <div className="flex w-full text-sm justify-between font-semibold">
                    <span className="text-[#333333]">Stats</span>
                    <button
                      role="button"
                      aria-hidden
                      onClick={() => setShowStats(!showStats)}
                      className="font-semibold underline cursor-pointer text-[#165FE3] disabled:opacity-40 disabled:cursor-not-allowed sm:min-h-[40px] sm:px-2"
                    >
                      {!showStats ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
                {/* Which product these figures are for. */}
                {!showStats && (
                  <div className="flex gap-1 px-3 lg:px-4 pt-3 border-b">
                    {statsCategories.map((tab) => (
                      <button
                        key={tab.value}
                        type="button"
                        onClick={() => setStatsCategory(tab.value)}
                        className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${
                          statsCategory === tab.value
                            ? "border-blue-700 text-blue-700"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex-1 overflow-auto p-3 lg:p-4 h-full">
                  {!showStats && (
                    <div className="flex flex-col lg:flex-row lg:flex-wrap gap-3">
                      {(statsForCategory ?? state?.stats)?.map((item: any, index: number) => {
                        return (
                          <div
                            key={index}
                            className="w-full lg:w-[48%] bg-white-A700 rounded-xl border gap-[14px] py-4 px-5"
                          >
                            <div className="bg-white-A700 flex flex-1 flex-col gap-[5px] items-start justify-center rounded-[10px] w-full">
                              <Text
                                className="text-blue-700 text-sm w-full"
                                size="txtInterSemiBold14"
                              >
                                {item.name}
                              </Text>
                              <Text
                                className=" text-gray-900"
                                size="txtInterSemiBold32"
                              >
                                {item.name === "Number of Orders"
                                  ? item.today
                                  : formatIndianNumber(
                                      parseFloat(item.today)?.toFixed(4),
                                    )}
                              </Text>
                              <div className="flex flex-row gap-[5px] items-center justify-between w-full">
                                <Text
                                  className="text-gray-600_01 text-xs w-auto"
                                  size="txtInterRegular12Gray60001"
                                >
                                  Today
                                </Text>
                                <Text
                                  className="text-gray-600_01 text-xs w-auto"
                                  size="txtInterSemiBold12"
                                >
                                  {item.name === "Number of Orders"
                                    ? item.today
                                    : "₹" +
                                      formatIndianNumber(
                                        parseFloat(item.today)?.toFixed(4),
                                      )}
                                </Text>
                              </div>
                              <div className="flex flex-row gap-[5px] items-center justify-between w-full">
                                <Text
                                  className="text-gray-600_01 text-xs w-auto"
                                  size="txtInterRegular12Gray60001"
                                >
                                  This Week
                                </Text>
                                <Text
                                  className="text-gray-600_01 text-xs w-auto"
                                  size="txtInterSemiBold12"
                                >
                                  {item.name === "Number of Orders"
                                    ? item.week
                                    : "₹" +
                                      formatIndianNumber(
                                        parseFloat(item.week)?.toFixed(4),
                                      )}
                                </Text>
                              </div>
                              <div className="flex flex-row gap-[5px] items-center justify-between w-full">
                                <Text
                                  className="text-gray-600_01 text-xs w-auto"
                                  size="txtInterRegular12Gray60001"
                                >
                                  This Month
                                </Text>
                                <Text
                                  className="text-gray-600_01 text-xs w-auto"
                                  size="txtInterSemiBold12"
                                >
                                  {item.name === "Number of Orders"
                                    ? item.month
                                    : "₹" +
                                      formatIndianNumber(
                                        parseFloat(item.month)?.toFixed(4),
                                      )}
                                </Text>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {state?.deal_stats?.map((item: any, index: number) => {
                        return (
                          <div
                            key={index}
                            className="w-full lg:w-[48%] bg-white-A700 rounded-xl border gap-[14px] py-4 px-5"
                          >
                            <div className="bg-white-A700 flex flex-1 flex-col gap-[5px] items-start justify-center rounded-[10px] w-full">
                              <Text
                                className="text-blue-700 text-sm w-full"
                                size="txtInterSemiBold14"
                              >
                                {item.name}
                              </Text>
                              <Text
                                className="md:text-3xl sm:text-[28px] text-[32px] text-red-500 w-[99px]"
                                size="txtInterSemiBold32"
                              >
                                {item.name === "Number of Orders"
                                  ? item.today
                                  : formatIndianNumber(
                                      parseFloat(item.today)?.toFixed(4),
                                    )}
                              </Text>
                              <div className="flex flex-row gap-[5px] items-center justify-between w-full">
                                <Text
                                  className="text-gray-600_01 text-xs w-auto"
                                  size="txtInterRegular12Gray60001"
                                >
                                  Today
                                </Text>
                                <Text
                                  className="text-red-500 text-xs w-auto"
                                  size="txtInterSemiBold12"
                                >
                                  {item.today}
                                </Text>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex-1 sm:w-full h-full overflow-hidden sm:overflow-auto bg-white-A700 rounded-xl">
              <div className="h-full overflow-auto p-4 wide-scroll">
                <PreparequoteHomepageLevel1 />
              </div>
            </div>
            {/* Phone only: the column order is reversed there, so this sits above the quote form. */}
            <div className="hidden sm:block">
              <ApplicationsCard />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DashboardPage;
